/**
 * CALENDAR UTILITIES
 * ==================
 * 
 * Utility functions for generating .ics calendar files for decision review reminders
 */

interface CalendarEvent {
  title: string;
  description: string;
  startDate: Date;
  endDate: Date;
  timezone: string;
}

/**
 * Format date for .ics format (YYYYMMDDTHHMMSSZ)
 */
function formatICSDate(date: Date): string {
  // Convert to the specified timezone for display but keep UTC for ICS format
  const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
  
  const year = utcDate.getFullYear();
  const month = String(utcDate.getMonth() + 1).padStart(2, '0');
  const day = String(utcDate.getDate()).padStart(2, '0');
  const hours = String(utcDate.getHours()).padStart(2, '0');
  const minutes = String(utcDate.getMinutes()).padStart(2, '0');
  const seconds = String(utcDate.getSeconds()).padStart(2, '0');
  
  return `${year}${month}${day}T${hours}${minutes}${seconds}Z`;
}

/**
 * Escape text for .ics format
 */
function escapeICSText(text: string): string {
  return text
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\n/g, '\\n')
    .replace(/\r/g, '');
}

/**
 * Generate UID for calendar event
 */
function generateUID(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}@decisionr.xyz`;
}

/**
 * Generate .ics file content for decision review reminders
 */
export function generateDecisionReviewCalendar(
  decisionTitle: string,
  decisionDate: Date,
  reviewIntervals: number[],
  timezone: string = 'UTC',
  decisionId?: string
): string {
  const events: CalendarEvent[] = reviewIntervals.map(interval => {
    const reviewDate = new Date(decisionDate);
    reviewDate.setDate(reviewDate.getDate() + interval);
    
    // Set time to 8 AM in user's timezone for better visibility
    reviewDate.setHours(8, 0, 0, 0);
    
    const endDate = new Date(reviewDate);
    endDate.setHours(8, 15, 0, 0); // 15-minute reminder event
    
    const decisionUrl = decisionId ? `https://decisionr.xyz/?decision=${decisionId}` : '';
    const urlText = decisionUrl ? `\n\nView your decision: ${decisionUrl}` : '';
    
    return {
      title: `Review Decision: ${decisionTitle}`,
      description: `Time to review your decision: "${decisionTitle}"\n\nIt's been ${interval} days since you made this decision. Take a moment to reflect on how it's going and rate your confidence.\n\nOriginal decision date: ${decisionDate.toLocaleDateString()}${urlText}`,
      startDate: reviewDate,
      endDate: endDate,
      timezone
    };
  });

  const now = new Date();
  const icsEvents = events.map(event => {
    const uid = generateUID();
    const dtstamp = formatICSDate(now);
    const dtstart = formatICSDate(event.startDate);
    const dtend = formatICSDate(event.endDate);
    
    return [
      'BEGIN:VEVENT',
      `UID:${uid}`,
      `DTSTAMP:${dtstamp}`,
      `DTSTART:${dtstart}`,
      `DTEND:${dtend}`,
      `SUMMARY:${escapeICSText(event.title)}`,
      `DESCRIPTION:${escapeICSText(event.description)}`,
      'CATEGORIES:DECISION-REVIEW',
      'BEGIN:VALARM',
      'TRIGGER:-PT1H',
      'ACTION:DISPLAY',
      `DESCRIPTION:${escapeICSText(`Reminder: ${event.title}`)}`,
      'END:VALARM',
      'END:VEVENT'
    ].join('\r\n');
  });

  const calendar = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Decisionr//Decision Review Reminders//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    ...icsEvents,
    'END:VCALENDAR'
  ].join('\r\n');

  return calendar;
}

/**
 * Download .ics file
 */
export function downloadICSFile(content: string, filename: string): void {
  const blob = new Blob([content], { type: 'text/calendar;charset=utf-8' });
  const url = window.URL.createObjectURL(blob);
  
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  window.URL.revokeObjectURL(url);
}
